/**
 * Sous-formulaire — infos tenant (sans branding ni admin SaaS).
 *
 * Whitelist 7.D limitée aux champs disponibles via `GET /v1/auth/me` :
 *   name, locale, timezone, currency, logo_url.
 *
 * Les champs `legal_name`, `email`, `phone`, `address` (objet) viendront
 * quand un endpoint `/v1/tenants/me` les exposera, ou quand /auth/me sera
 * étendu — chunk dédié.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type TenantInfoFormState = {
  name: string;
  locale: "fr" | "en";
  timezone: string;
  currency: string;
  logo_url: string;
  logo_upload?: { mime_type: "image/png" | "image/jpeg"; content_base64: string };
};

type Props = {
  value: TenantInfoFormState;
  onChange: (next: TenantInfoFormState) => void;
  disabled?: boolean;
  tenantId?: string;
};

export function TenantInfoForm({ value, onChange, disabled, tenantId }: Props) {
  const { t } = useTranslation();
  const [logoError, setLogoError] = useState(false);
  const latest = useRef({ value, tenantId });
  latest.current = { value, tenantId };
  const readSequence = useRef(0);
  useEffect(
    () => () => {
      readSequence.current += 1;
    },
    [],
  );
  let preview: string | undefined;
  if (value.logo_upload)
    preview = `data:${value.logo_upload.mime_type};base64,${value.logo_upload.content_base64}`;
  else if (value.logo_url && tenantId) {
    try {
      const url = new URL(value.logo_url);
      if (
        url.origin === new URL(import.meta.env.VITE_SUPABASE_URL).origin &&
        url.pathname.startsWith(
          `/storage/v1/object/public/kollectivia-tenant-logos/${tenantId}/brand/`,
        ) &&
        /\/[a-f0-9]{64}\.(png|jpg)$/.test(url.pathname) &&
        !url.search &&
        !url.username &&
        !url.password
      )
        preview = url.href;
    } catch {
      /* Existing unsupported logos use the cabinet text identity. */
    }
  }

  const set = <K extends keyof TenantInfoFormState>(key: K, v: TenantInfoFormState[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="md:col-span-2">
        <label className="label" htmlFor="tenant-name">
          {t("settings.fields.name")}
        </label>
        <input
          id="tenant-name"
          className="input"
          type="text"
          value={value.name}
          onChange={(e) => set("name", e.target.value)}
          disabled={disabled}
          required
          minLength={2}
          maxLength={200}
        />
      </div>

      <div>
        <label className="label" htmlFor="tenant-locale">
          {t("settings.fields.locale")}
        </label>
        <select
          id="tenant-locale"
          className="input"
          value={value.locale}
          onChange={(e) => set("locale", e.target.value as "fr" | "en")}
          disabled={disabled}
        >
          <option value="fr">Français</option>
          <option value="en">English</option>
        </select>
      </div>

      <div>
        <label className="label" htmlFor="tenant-currency">
          {t("settings.fields.currency")}
        </label>
        <input
          id="tenant-currency"
          className="input"
          type="text"
          value={value.currency}
          onChange={(e) => set("currency", e.target.value.toUpperCase())}
          disabled={disabled}
          maxLength={3}
          minLength={3}
          placeholder="CAD"
        />
      </div>

      <div className="md:col-span-2">
        <label className="label" htmlFor="tenant-timezone">
          {t("settings.fields.timezone")}
        </label>
        <input
          id="tenant-timezone"
          className="input"
          type="text"
          value={value.timezone}
          onChange={(e) => set("timezone", e.target.value)}
          disabled={disabled}
          placeholder="America/Montreal"
        />
      </div>

      <div className="md:col-span-2">
        <label className="label" htmlFor="tenant-logo-file">
          {t("settings.logo.choose")}
        </label>
        <input
          id="tenant-logo-file"
          className="input min-h-11 w-full"
          type="file"
          accept="image/png,image/jpeg"
          disabled={disabled}
          aria-describedby="tenant-logo-help"
          onChange={(event) => {
            const sequence = ++readSequence.current;
            const file = event.target.files?.[0];
            if (!file) return;
            if (
              !["image/png", "image/jpeg"].includes(file.type) ||
              file.size > 1048576 ||
              !file.size
            ) {
              setLogoError(true);
              event.target.value = "";
              return;
            }
            const reader = new FileReader();
            reader.onerror = () => {
              if (sequence === readSequence.current && latest.current.tenantId === tenantId)
                setLogoError(true);
            };
            reader.onload = () => {
              if (sequence !== readSequence.current || latest.current.tenantId !== tenantId) return;
              const data = String(reader.result).split(",")[1];
              if (!data) {
                setLogoError(true);
                return;
              }
              setLogoError(false);
              onChange({
                ...latest.current.value,
                logo_upload: {
                  mime_type: file.type as "image/png" | "image/jpeg",
                  content_base64: data,
                },
              });
            };
            reader.readAsDataURL(file);
          }}
        />
        <p id="tenant-logo-help" className="mt-1 text-xs text-slate-600">
          {t("settings.logo.help")}
        </p>
        {logoError && (
          <p role="alert" className="mt-2 text-sm text-red-700">
            {t("settings.logo.invalid")}
          </p>
        )}
        {preview ? (
          <img
            src={preview}
            alt={t("settings.logo.preview")}
            width={160}
            height={80}
            className="mt-3 h-20 w-40 object-contain"
          />
        ) : (
          <p className="mt-2 text-sm text-slate-600">
            {t(value.logo_url ? "settings.logo.unsupported" : "settings.logo.empty")}
          </p>
        )}
        {(value.logo_url || value.logo_upload) && (
          <button
            type="button"
            className="btn mt-2 min-h-11"
            disabled={disabled}
            onClick={() => {
              readSequence.current += 1;
              setLogoError(false);
              onChange({ ...value, logo_url: "", logo_upload: undefined });
            }}
          >
            {t("settings.logo.remove")}
          </button>
        )}
      </div>
    </div>
  );
}
